import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Inbox, Search, Clock, Eye, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { governanceAPI } from '../../api';
import { toast } from 'sonner';

const RequestsTab = ({ requests, searchTerm, setSearchTerm, fetchData }) => {
  const [requestActionId, setRequestActionId] = useState(null);
  const [requestNotes, setRequestNotes] = useState('');
  const [savingRequest, setSavingRequest] = useState(false);

  const handleUpdateRequestStatus = async (requestId, status) => {
    setSavingRequest(true);
    try {
      await governanceAPI.updateRequestStatus(requestId, { status, admin_notes: requestNotes });
      toast.success(`Request ${status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'updated'}`);
      setRequestActionId(null);
      setRequestNotes('');
      fetchData();
    } catch { toast.error('Failed to update request'); }
    finally { setSavingRequest(false); }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800 border-amber-300',
    in_review: 'bg-blue-100 text-blue-800 border-blue-300',
    approved: 'bg-green-100 text-green-800 border-green-300',
    rejected: 'bg-red-100 text-red-800 border-red-300',
  };
  const statusIcons = { pending: Clock, in_review: Eye, approved: CheckCircle, rejected: XCircle };
  const priorityColors = { urgent: 'text-red-600', high: 'text-orange-600', normal: 'text-[#5A6570]', low: 'text-[#A8B1B9]' };
  const typeLabels = { general: 'General', document_access: 'Document Access', meeting_request: 'Meeting Request', extension: 'Extension', other: 'Other' };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input placeholder="Search requests..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-requests-hub" />
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-[#A8B1B9]">{requests.filter(r => r.status === 'pending').length} pending of {requests.length} total</p>
        </div>
      </div>
      {requests.length === 0 ? (
        <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
          <Inbox className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
          <h3 className="text-base font-medium text-[#1A1D21] mb-1">No requests yet</h3>
          <p className="text-sm text-[#5A6570]">Stakeholder requests will appear here once the Stakeholders Portal is active</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {requests.filter(req => (req.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) || (req.details || '').toLowerCase().includes(searchTerm.toLowerCase()) || (req.submitted_by_name || '').toLowerCase().includes(searchTerm.toLowerCase())).map(req => {
            const StatusIcon = statusIcons[req.status] || Clock;
            return (
              <Card key={req.id} className={`border-[#E2E6EA] ${req.status === 'pending' ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`request-${req.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-sm font-semibold text-[#1A1D21]">{req.subject}</h3>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${statusColors[req.status] || ''}`}>
                          <StatusIcon className="h-3 w-3" />{req.status?.replace('_', ' ')}
                        </span>
                        <Badge variant="outline" className="text-[10px]">{typeLabels[req.request_type] || req.request_type}</Badge>
                        {req.priority && req.priority !== 'normal' && (
                          <span className={`text-[10px] font-medium uppercase ${priorityColors[req.priority] || ''}`}>{req.priority}</span>
                        )}
                      </div>
                      {req.details && <p className="text-xs text-[#5A6570] mt-1 line-clamp-2">{req.details}</p>}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-[10px] text-[#A8B1B9]">
                        <span>From: {req.submitted_by_name || req.submitted_by_email}</span>
                        <span>{req.created_at ? format(parseISO(req.created_at), 'MMM d, yyyy h:mm a') : ''}</span>
                        {req.reviewed_by_name && <span>Reviewed by: {req.reviewed_by_name}</span>}
                      </div>
                      {req.admin_notes && (
                        <div className="mt-2 p-2 bg-[#F8F9FA] rounded text-xs text-[#5A6570]">
                          <span className="font-medium">Notes:</span> {req.admin_notes}
                        </div>
                      )}
                    </div>
                    {req.status === 'pending' && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleUpdateRequestStatus(req.id, 'approved')} data-testid={`approve-request-${req.id}`}>
                          <CheckCircle className="h-3 w-3 mr-1" />Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => { setRequestActionId(req.id); setRequestNotes(''); }} data-testid={`reject-request-${req.id}`}>
                          <XCircle className="h-3 w-3 mr-1" />Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Reject Request Dialog */}
      <Dialog open={!!requestActionId} onOpenChange={v => { if (!v) { setRequestActionId(null); setRequestNotes(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reject Request</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Reason / Notes</Label>
            <Textarea value={requestNotes} onChange={e => setRequestNotes(e.target.value)} placeholder="Provide a reason for rejection..." rows={3} className="border-[#E2E6EA]" data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRequestActionId(null); setRequestNotes(''); }}>Cancel</Button>
            <Button onClick={() => handleUpdateRequestStatus(requestActionId, 'rejected')} disabled={savingRequest}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-request-btn">
              {savingRequest && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestsTab;
